import type { VendorCode } from 'utils/vendorDisplayName'

/**
 * Canonical row for filter engine (dashboard + normalized scheduler).
 * Optional fields stay optional so new vendors/sources can extend without breaking matches.
 */
export type FilterableRecord = {
  vendor: VendorCode
  robotSn: string
  robotId: number | null
  /** Facility ids for this robot (enriched from FMS facility ↔ robot mapping). */
  facilityIds: number[]
  missionId: number | null
  scenarioId: number | null
  mapId: string | null
  missionsInfoIds: number[]
  /** Optional labels from API missions_info (for mission dropdown). */
  missionsInfo?: Array<{ id: number; name?: string }>
  /** Upstream id: MissionJob.id, Odyssey.id, GS report UUID, etc. */
  taskId: number | string | null
  displayName: string
  startAt: Date
  endAt: Date | null
  /** Original API event_id for stable calendar keys */
  sourceEventId: string
  vendorOriginRecordType?: string | null
  missionsProgress?: any[]
  overallProgress?: Record<string, any> | null
  executionStatus?: number | null
}

/** Raw row from GET .../tasks-normalized/ or dashboard-normalized (record_to_api_dict). */
export type NormalizedStatisticsApiRow = {
  event_id: string
  robot_sn: string
  name: string
  start_time: string
  created_at: string
  end_time?: string | null
  vendor?: VendorCode | null
  vendor_origin_record_type?: string | null
  vendor_origin_record_id?: number | string | null
  robot_id?: number | null
  scenario_or_map_id?: number | string | null
  mission_or_area_id?: number | string | null
  scenario_id?: number | null
  missions_info?: Array<{ id?: number | string; name?: string }>
  duration_actual?: number | null
  duration_overall?: number | null
  cleaned_area_actual?: number | null
  cleaned_area_planned?: number | null
  cleaning_performance?: number | null
  battery_begin?: number | null
  battery_end?: number | null
  completion_percentage?: number | null
  travel_distance_actual?: number | null
  travel_distance_planned?: number | null
  water_consumption?: number | null
  water_consumption_unit?: string | null
  dirty_water_consumption?: number | null
  dirty_water_consumption_unit?: string | null
  garbage_filled_begin?: number | null
  garbage_filled_end?: number | null
  missions_progress?: any[]
  execution_status?: number | null
}

export const FACILITY_NONE_ID = '__none__'

export type TasksNormalizedFilterState = {
  vendor: VendorCode | ''
  facilityId: string
  robotSn: string
  /** Numeric id string, or `map:...` for Gausium maps */
  missionKey: string
  /** -1 = no filter */
  startAfterHour: number
}

export type RobotOption = { id: number; name: string; sn: string }

export type FilterEngineContext = {
  facilityMappingReady: boolean
  facilities: Array<{ id: number; name: string }>
  robotsBySn: Map<string, RobotOption>
}

export type MissionOption = {
  key: string
  label: string
  kind: 'scenario' | 'mission' | 'map'
}
